import React, { useState, useEffect } from 'react';
import { RefreshCw, Download } from 'lucide-react';
import { RiAiGenerate } from 'react-icons/ri';
import { SearchSelect } from '../modules/FieldComponents';
import Alert from '../modules/Alert';
import { useGenerationReview } from '../modules/GenerationReview';

export const getReportFusionTemplateType = (template = {}) => String(
    template.s3_key || template.name || ''
).split('.').pop().toLowerCase();

const formatLastModified = (dateString) => {
    if (!dateString || typeof dateString !== 'string') return ["", "", "", "", "", ""];

    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    try {
        const hasTimeZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(dateString);
        const isDateTimeWithSpace = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString);
        const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
        const isIsoWithoutZone = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(dateString);

        let normalized = dateString;
        if (!hasTimeZone) {
            if (isDateTimeWithSpace) {
                normalized = `${dateString.replace(' ', 'T')}Z`;
            } else if (isDateOnly) {
                normalized = `${dateString}T00:00:00Z`;
            } else if (isIsoWithoutZone) {
                normalized = `${dateString}Z`;
            }
        }

        let parsedDate = new Date(normalized);

        if (isNaN(parsedDate.getTime())) {
            parsedDate = new Date(`${dateString}T00:00:00Z`);
        }

        if (isNaN(parsedDate.getTime())) {
            return ["", "", "", "", "", ""];
        }

        const fallbackTimeZone = "America/Chicago";
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || fallbackTimeZone;
        let zonedTime;
        try {
            zonedTime = new Date(parsedDate.toLocaleString("en-US", { timeZone: userTimeZone }));
        } catch (e) {
            zonedTime = new Date(parsedDate.toLocaleString("en-US", { timeZone: fallbackTimeZone }));
        }

        const iso = parsedDate.toISOString();
        const longDate = `${months[zonedTime.getMonth()]} ${zonedTime.getDate()}, ${zonedTime.getFullYear()}`;
        const shortDate = `${zonedTime.getMonth() + 1}/${zonedTime.getDate()}/${zonedTime.getFullYear()}`;
        const shortFormatted = `${zonedTime.getDate()} ${months[zonedTime.getMonth()].slice(0, 3)} ${zonedTime.getFullYear()}`;
        const hours = zonedTime.getHours();
        const minutes = zonedTime.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const hour12 = hours % 12 || 12;
        const minutePadded = String(minutes).padStart(2, '0');
        const timeFormatted = `${hour12}:${minutePadded} ${ampm}`;
        const dateTimeFormatted = `${zonedTime.getMonth() + 1}/${zonedTime.getDate()}/${zonedTime.getFullYear()} ${timeFormatted}`;

        return [longDate, iso, timeFormatted, shortDate, shortFormatted, dateTimeFormatted];
    } catch (e) {
        console.error("Invalid date:", dateString, e);
        return ["", "", "", "", "", ""];
    }
};

export const ReportFusion = ({ 
    field, 
    case_id, 
    section_id,
    handleFieldChange, 
    value
}) => {
    const generationReview = useGenerationReview();
    const [selectedTemplate, setSelectedTemplate] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [availableTemplates, setAvailableTemplates] = useState([]);
    const [templateData, setTemplateData] = useState([]);
    const [message, setMessage] = useState(null);
    const [generatedFile, setGeneratedFile] = useState(null);
    const ownsActiveReview = Boolean(generationReview?.active)
        && Boolean(generationReview?.matchesPendingReview?.({
            engine: 'report_fusion',
            fieldId: field?.id,
            templateName: selectedTemplate,
        }));
    const anotherReviewIsActive = Boolean(generationReview?.active) && !ownsActiveReview;

    // Load available ReportFusion templates from S3
    useEffect(() => {
        const loadTemplates = async () => {
            try {
                const response = await fetch('https://api.casedb.co/report-fusion-templates.php', {
                    method: 'GET',
                    credentials: 'include',
                });
                
                const result = await response.json();
                
                console.log('ReportFusion templates response:', result);
                
                if (result.success) {
                    // Create a simple array of template names for SearchSelect
                    // SearchSelect will convert strings to {label, value} format internally
                    const templateNames = result.templates.map(template => template.name);
                    
                    console.log('Template names for SearchSelect:', templateNames);
                    setAvailableTemplates(templateNames);
                    
                    // Store the full template data separately for lookup
                    setTemplateData(result.templates);
                } else {
                    console.error('Failed to load ReportFusion templates:', result.message);
                    setAvailableTemplates([]);
                }
            } catch (error) {
                console.error('Error loading ReportFusion templates:', error);
            }
        };

        loadTemplates();
    }, []);

    // If value is a file ID (numeric or numeric string), convert to file object
    // For now we'll handle both numeric IDs and file objects
    useEffect(() => {
        if (!value) {
            setGeneratedFile(null);
            return;
        }

        if (value && typeof value === 'object' && value.id) {
            setGeneratedFile(value);
            return;
        }

        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                if (parsed && typeof parsed === 'object' && parsed.id) {
                    setGeneratedFile(parsed);
                    return;
                }
            } catch (e) {
                // Not JSON, fall through and clear local generated-file state
            }
        }

        setGeneratedFile(null);
    }, [value]);

    const downloadFile = async (file, filename) => {
        try {
            const fileId = Number(file?.id || file?.file_id || 0);
            const fileKey = String(file?.file_path || file?.key || file?.path || '').trim();

            if (!fileId && !fileKey) {
                throw new Error('Missing file identifier');
            }

            const downloadName = filename || file?.filename || file?.file_name || file?.name || 'download';
            const params = new URLSearchParams({
                cases: 'true',
                download: 'true',
                download_name: downloadName
            });
            if (fileId > 0) {
                params.set('id', String(fileId));
            } else {
                params.set('key', fileKey);
            }

            const response = await fetch(`https://api.casedb.co/get-signed-url.php?${params.toString()}`, {
                credentials: 'include'
            });
            const payload = await response.json();

            if (!response.ok || !payload?.success || !payload?.url) {
                throw new Error(payload?.message || 'Failed to get download URL');
            }

            const link = document.createElement('a');
            link.href = payload.url;
            link.download = payload.filename || downloadName;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Download error:', error);
            alert('Failed to download file');
        }
    };

    const generateAndUploadDocument = async (templateOverride = null) => {
        const templateName = typeof templateOverride === 'string' ? templateOverride : selectedTemplate;
        if (!templateName || !field.id || !case_id) {
            console.error('Missing required parameters for ReportFusion generation');
            return;
        }

        // Find the selected template's S3 key
        const selectedTemplateData = templateData.find(template => template.name === templateName);
        if (!selectedTemplateData) {
            console.error('Could not find template data for:', templateName);
            alert('Template not found. Please try again.');
            return;
        }

        const templateType = getReportFusionTemplateType(selectedTemplateData);
        const basePayload = {
            field_id: field.id,
            case_id: case_id,
            template_s3_key: selectedTemplateData.s3_key
        };

        if (generationReview?.enabled && templateType === 'xlsx') {
            setIsGenerating(true);
            setMessage(null);
            try {
                await generationReview.startReview({
                    engine: 'report_fusion',
                    endpoint: 'https://api.casedb.co/report-fusion.php',
                    templateName: selectedTemplateData.name,
                    templateType,
                    previewPayload: { ...basePayload, action: 'preflight' },
                    finalizePayload: { ...basePayload, action: 'finalize' },
                    directPayload: basePayload,
                    source: {
                        sectionId: Number(section_id || 0) || null,
                        fieldId: Number(field.id),
                        groupId: null,
                    },
                    onGenerated: (result) => {
                        if (!result?.file) return;
                        setGeneratedFile(result.file);
                        handleFieldChange?.(field.id, result.file);
                        setSelectedTemplate('');
                    },
                });
            } catch (error) {
                if (!error?.generationReviewHandled) {
                    setMessage({
                        message: error.message || 'The document data could not be reviewed.',
                        type: 'error'
                    });
                }
            } finally {
                setIsGenerating(false);
            }
            return;
        }

        setIsGenerating(true);
        
        try {
            const response = await fetch('https://api.casedb.co/report-fusion.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    field_id: field.id,
                    case_id: case_id,
                    template_s3_key: selectedTemplateData.s3_key
                })
            });

            const contentType = response.headers.get('content-type');
            
            if (contentType && contentType.includes('application/json')) {
                // New response format: JSON with file object
                const result = await response.json();
                
                if (result.success && result.file) {
                    console.log('ReportFusion generated successfully:', result.file);
                    
                    // Store the file object
                    setGeneratedFile(result.file);
                    
                    // Update the field with the file object so it persists
                    handleFieldChange?.(field.id, result.file);
                    
                    // Show success message
                    setMessage({ message: 'ReportFusion successfully generated!', type: 'success' });
                    
                    // Clear the selected template
                    setSelectedTemplate("");
                } else {
                    console.error('ReportFusion generation failed:', result.message);
                    setMessage({ message: `Failed to generate ReportFusion: ${result.message}`, type: 'error' });
                }
            } else {
                // Old response format: direct download (backward compatibility)
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                
                // Extract filename from Content-Disposition header
                const contentDisposition = response.headers.get('content-disposition');
                let filename = 'ReportFusion_Document';
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                    if (filenameMatch) {
                        filename = filenameMatch[1];
                    }
                } else {
                    // Fallback extension based on content-type
                    if (contentType.includes('spreadsheetml')) filename += '.xlsx';
                    else if (contentType.includes('wordprocessingml')) filename += '.docx';
                    else filename += '.bin';
                }
                
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                
                // Update the field with the filename
                handleFieldChange?.(field.id, filename);
                console.log('ReportFusion downloaded successfully:', filename);

                setMessage({ message: 'ReportFusion successfully generated. Documents -> Settlement (folder)', type: 'success' });
                
                // Clear the selected template
                setSelectedTemplate("");
            }
        } catch (error) {
            console.error('Error generating ReportFusion:', error);
            setMessage({ message: 'Error generating ReportFusion. Please try again.', type: 'error' });
        } finally {
            setIsGenerating(false);
        }
    };

    // If we have a generated file object, display it with download capability
    if (generatedFile && typeof generatedFile === 'object' && generatedFile.id) {
        const fileSize = generatedFile.size ? `${(generatedFile.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size';
        const filename = generatedFile.filename || generatedFile.file_name || 'Report';
        const [, , timeFormatted, , shortFormatted] = formatLastModified(generatedFile.uploaded_date);
        const displayDate = shortFormatted ? `${shortFormatted} ${timeFormatted}` : new Date().toLocaleDateString();
        
        return (
            <>
                <div className='doc-gen'>
                    <div className="generated-document">
                        <div className='docgen-icon' style={{ marginTop: '2px' }}>
                            <RiAiGenerate size={18} style={{ flexShrink: 0 }} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', justifyContent: 'center', alignItems: 'flex-start', flex: 1, minWidth: 0, minHeight: '36px' }}>
                            <div 
                                className="small-text" 
                                style={{ fontWeight: '500', whiteSpace: 'normal', overflow: 'hidden', cursor: 'pointer', color: 'var(--fill)', textDecoration: 'underline' }} 
                                title={filename}
                                onClick={() => window.open(`/file/${generatedFile.id}`, '_blank')}
                            >
                                {filename}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-color-secondary)' }}>
                                {fileSize} • {displayDate}
                            </div>
                        </div>
                        <button 
                            className='action alt micro'
                            onClick={() => downloadFile(generatedFile, filename)}
                            title="Download file"
                            style={{ flexShrink: 0 }}
                        >
                            <Download size={16} />
                        </button>
                        <button 
                            className='action alt micro' 
                            onClick={() => {
                                setGeneratedFile(null);
                                handleFieldChange?.(field.id, '');
                                setSelectedTemplate("");
                            }}
                            title="Clear and generate new ReportFusion"
                            style={{ flexShrink: 0 }}
                        >
                            <RefreshCw size={16} />
                        </button>
                    </div>
                </div>
                {message && (<Alert message={message.message} type={message.type} onClose={() => setMessage(null)} />)}
            </>
        );
    }

    // If we have a generated document filename string (backward compatibility)
    if (value && typeof value === 'string' && value.trim() !== '') {
        return (
            <>
                <div className='doc-gen'>
                    <div className="generated-document">
                        <span className="subtext" style={{ width: "100%", backgroundColor: "var(--secondary-color)", borderRadius: '6px', border: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 10px' }}>{value}</span>
                        <button 
                            className='action alt micro'
                            onClick={() => {
                                handleFieldChange?.(field.id, '');
                                setSelectedTemplate("");
                            }}
                            title="Clear and generate new ReportFusion"
                        >
                            <RefreshCw size={16} />
                        </button>
                    </div>
                </div>
                {message && (<Alert message={message.message} type={message.type} onClose={() => setMessage(null)} />)}
            </>
        );
    }

    // Otherwise, show the template selector
    return (
        <>
            <div className='doc-gen'>
                <SearchSelect
                    value={selectedTemplate}
                    disabled={isGenerating || Boolean(generationReview?.active)}
                    onChange={(val) => {
                        setSelectedTemplate(val);
                        const nextTemplate = templateData.find(template => template.name === val);
                        const nextTemplateType = getReportFusionTemplateType(nextTemplate);
                        if (val && generationReview?.enabled && nextTemplateType === 'xlsx') {
                            generateAndUploadDocument(val);
                        }
                    }}
                    options={availableTemplates}
                    placeholder="Select a ReportFusion template..."
                />
                <button 
                    className='action alt micro' 
                    disabled={!selectedTemplate || anotherReviewIsActive || (isGenerating && !ownsActiveReview)}
                    onClick={() => {
                        if (ownsActiveReview) {
                            generationReview.requestDirectGeneration?.({
                                engine: 'report_fusion',
                                fieldId: field.id,
                                templateName: selectedTemplate,
                            });
                            return;
                        }
                        generateAndUploadDocument();
                    }}
                >
                    <RiAiGenerate size={18} title="Generate ReportFusion" disabled={isGenerating} />
                </button>
            </div>
            {message && (<Alert message={message.message} type={message.type} onClose={() => setMessage(null)} />)}
        </>
    );
};
